import React, {
  useCallback,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo
} from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import {
  Row,
  Col,
  Table,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Button,
  Badge
} from 'reactstrap';
import FlexSelect from 'components/Inputs/FlexSelect';
import CurrencyMask from 'components/Inputs/CurrencyMask';
import { toast } from 'react-toastify';
import priceCatalogService from 'views/CRM/PriceCatalog/service/priceCatalogService';

import ProductChangeIndicator from './components/ProductChangeIndicator';
import html2pdf from 'html2pdf.js';
import CatalogBudgetPrintable from './components/CatalogBudgetPrintable';
import ChangesDetailModal from './components/ChangeDetailsModal/index';
import { AlertBox } from './components/ChangeDetailsModal/styled';
import { removeFields } from 'utils/fuctions/removeObjectsFields';
import ProductsFieldsInfModal from './components/ProductsDetailsModal';
import ProposalService from 'views/CRM/proposal/service';
import { processProposalVariables } from 'views/CRM/proposal/utils/processProposalVariables';
import CustomFieldsService from 'views/fieldConfigs/service';

const ACTION_TYPES = {
  CHANGE_QUANTITY: 'CHANGE_QUANTITY',
  CHANGE_DISCOUNT: 'CHANGE_DISCOUNT',
  CHANGE_SUBTOTAL: 'CHANGE_SUBTOTAL',
  CHANGE_UNIT_PRICE: 'CHANGE_UNIT_PRICE'
};

const CatalogProductsForm = forwardRef(
  (
    {
      watch,
      getValues,
      control,
      setValue,
      isEditing = false,
      catalogData = null,
      setCatalogData = () => {},
      proposalId = null,
      accountData = null,
      contactData = null
    },
    ref
  ) => {
    const [detailModalState, setDetailModalState] = useState({
      isOpen: false,
      productId: null
    });
    const [priceCatalogs, setPriceCatalogs] = useState([]);
    const [discountMatrix, setDiscountMatrix] = useState([]);
    const [products, setProducts] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isProductInitialized, setIsProductInitialized] = useState(false);
    const [productChanges, setProductChanges] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [catalogChangesHistory, setCatalogChangesHistory] = useState([]);
    const [showChangesDetail, setShowChangesDetail] = useState(false);
    const [useUpdatedDiscountMatrix, setUseUpdatedDiscountMatrix] =
      useState(false);
    const [pdfData, setPdfData] = useState(null);
    const [renderPdf, setRenderPdf] = useState(false);
    const printRef = React.useRef(null);
    const [proposalData, setProposalData] = useState(null);

    useForm({
      defaultValues: {
        productItems: []
      }
    });

    const { fields, append, remove } = useFieldArray({
      control,
      name: 'productItems'
    });

    const watchCatalogId = watch('catalogId');
    const watchProductItems = watch('productItems');
    const watchSelectedProducts = watch('products');
    const controlDiscountOnlyOnSum = watch('discountOnlyOnSum');
    const controlIsToUseDiscountOnTotalValue = watch(
      'isToUseDiscountOnTotalValue'
    );

    // Cuida das mudanças dos valores na seção de "Produtos Selecionados"
    const useProductTableReducer = (setValue, getValues) => {
      const dispatch = useCallback(
        (action) => {
          const { type, index, value, onChange } = action;
          const getSafeNumber = (val) => parseFloat(val) || 0;
          const quantity = parseInt(
            getValues(`productItems[${index}].quantity`) || 1
          );
          const catalogPrice = getSafeNumber(
            getValues(`productItems[${index}].price`)
          );

          const matrixDiscountPercent = getMatrixDiscountForQuantity(
            index,
            quantity
          );
          const matrixFactor = matrixDiscountPercent / 100;

          const priceAfterMatrix = catalogPrice * (1 - matrixFactor);

          switch (type) {
            case ACTION_TYPES.CHANGE_QUANTITY: {
              const rawValue = value;
              onChange(rawValue);

              const safeQuantity = Math.max(1, parseInt(rawValue) || 1);

              const discount = parseFloat(
                getValues(`productItems[${index}].discount`) || 0
              );

              const subtotal = calculateItemSubtotal(
                index,
                safeQuantity,
                discount
              );

              setValue(`productItems[${index}].subtotal`, subtotal);
              break;
            }

            case ACTION_TYPES.CHANGE_DISCOUNT: {
              onChange(value);

              const safeDiscount = Math.min(
                100,
                Math.max(-9999, getSafeNumber(value))
              );

              const manualFactor = safeDiscount / 100;
              const newUnitPrice = priceAfterMatrix * (1 - manualFactor);
              const newSubtotal = newUnitPrice * quantity;

              setValue(
                `productItems[${index}].unitPrice`,
                newUnitPrice.toFixed(2)
              );
              setValue(
                `productItems[${index}].subtotal`,
                newSubtotal.toFixed(2)
              );
              break;
            }

            case ACTION_TYPES.CHANGE_SUBTOTAL: {
              onChange(value);

              const safeSubtotal = Math.max(0, getSafeNumber(value));

              const newUnitPrice = quantity > 0 ? safeSubtotal / quantity : 0;

              setValue(
                `productItems[${index}].unitPrice`,
                newUnitPrice.toFixed(2)
              );

              const totalWithMatrixOnly = priceAfterMatrix * quantity;

              if (totalWithMatrixOnly > 0) {
                const calculatedDiscount =
                  (1 - safeSubtotal / totalWithMatrixOnly) * 100;

                setValue(
                  `productItems[${index}].discount`,
                  calculatedDiscount.toFixed(2)
                );
              } else {
                setValue(`productItems[${index}].discount`, '0.00');
              }
              break;
            }

            case ACTION_TYPES.CHANGE_UNIT_PRICE: {
              onChange(value);
              const safeUnitPrice = Math.max(0, getSafeNumber(value));

              let calculatedDiscount = 0;

              if (priceAfterMatrix > 0) {
                calculatedDiscount =
                  ((priceAfterMatrix - safeUnitPrice) / priceAfterMatrix) * 100;
              }

              const newSubtotal = safeUnitPrice * quantity;

              setValue(
                `productItems[${index}].discount`,
                calculatedDiscount.toFixed(2)
              );
              setValue(
                `productItems[${index}].subtotal`,
                newSubtotal.toFixed(2)
              );
              break;
            }

            default:
              break;
          }
        },
        [setValue, getValues, discountMatrix]
      );

      return dispatch;
    };

    const dispatchProductAction = useProductTableReducer(setValue, getValues);

    useImperativeHandle(ref, () => ({
      formatProductsAndCatalogsToShow: formatProductsAndCatalogsToShow,
      forceUpdate: () => {
        setIsInitialized(false);
        setIsProductInitialized(false);
      }
    }));

    const getCatalogs = useCallback(() => {
      priceCatalogService
        .getAllPriceCatalogs()
        .then((response) => {
          setPriceCatalogs(response.data.data);
        })
        .catch(() => {
          toast.error('Ocorreu um erro ao buscar os catálogos de preço');
        });
    }, []);

    const getDiscountMatrixByCatalogId = useCallback((catalogId) => {
      priceCatalogService
        .getDiscountMatrixByCatalogId(catalogId)
        .then((response) => {
          setDiscountMatrix(response.data.data);
        })
        .catch(() => {
          toast.error('Ocorreu um erro ao buscar a matriz de desconto');
        });
    }, []);

    const getProductsByPriceCatalogId = useCallback((catalogId) => {
      priceCatalogService
        .getProductsByPriceCatalogId(catalogId, false)
        .then((response) => {
          setProducts(response.data.data);
        })
        .catch(() => {
          toast.error('Ocorreu um erro ao buscar os produtos do catálogo');
        });
    }, []);

    const getCatalogChangesHistory = useCallback(() => {
      if (
        isEditing &&
        catalogData &&
        catalogData.catalogId &&
        hasChanges &&
        showChangesDetail
      )
        priceCatalogService
          .getCatalogChangesHistory(catalogData.catalogId)
          .then((response) => {
            setCatalogChangesHistory(response.data.data);
          })
          .catch(() => {
            toast.error(
              'Ocorreu um erro ao buscar o histórico de alterações do catálogo'
            );
          });
    }, [catalogData?.catalogId, isEditing, hasChanges, showChangesDetail]);

    const areMatricesEqual = (matrix1, matrix2) => {
      if (matrix1.length !== matrix2.length) return false;

      const sortedMatrix1 = [...matrix1].sort((a, b) => a.id - b.id);
      const sortedMatrix2 = [...matrix2].sort((a, b) => a.id - b.id);

      return sortedMatrix1.every((item, index) => {
        const other = sortedMatrix2[index];
        return (
          item.id === other.id &&
          item.type === other.type &&
          item.discount === other.discount &&
          item.maxQuantity === other.maxQuantity &&
          item.minQuantity === other.minQuantity
        );
      });
    };

    const detectChanges = useCallback(
      (
        historicalProducts,
        currentProducts,
        historicalDiscountMatrix,
        currentDiscountMatrix
      ) => {
        const productChanges = historicalProducts.map((histProduct) => {
          const currentProduct = currentProducts.find(
            (p) => p.id === histProduct.productId
          );

          if (!currentProduct) {
            return {
              ...histProduct,
              status: 'removed',
              changeType: 'removed',
              statusText: 'Removido do catálogo'
            };
          }

          const changes = [];
          if (
            parseFloat(currentProduct.catalogPrice) !==
            parseFloat(histProduct.basePrice)
          ) {
            changes.push({
              field: 'price',
              oldValue: parseFloat(histProduct.basePrice),
              newValue: parseFloat(currentProduct.catalogPrice)
            });
          }

          if (currentProduct.name !== histProduct.productName) {
            changes.push({
              field: 'name',
              oldValue: histProduct.productName,
              newValue: currentProduct.name
            });
          }

          if (changes.length > 0) {
            return {
              ...histProduct,
              status: 'changed',
              changeType: 'changed',
              statusText: 'Alterado no catálogo',
              changes,
              currentData: currentProduct
            };
          }

          return { ...histProduct, status: 'unchanged' };
        });

        const discountMatrixChanged = !areMatricesEqual(
          historicalDiscountMatrix,
          currentDiscountMatrix
        );

        return {
          productChanges,
          discountMatrixChanged
        };
      },
      []
    );

    useEffect(() => {
      if (watchCatalogId && catalogData?.catalogId !== watchCatalogId) {
        return;
      }
      if (
        isEditing &&
        catalogData &&
        products.length > 0 &&
        discountMatrix.length > 0
      ) {
        const { productChanges: newProductChanges, discountMatrixChanged } =
          detectChanges(
            catalogData.products,
            products,
            catalogData.priceMatrix,
            discountMatrix
          );

        const allChanges = [...newProductChanges];
        if (discountMatrixChanged) {
          allChanges.push({
            status: 'changed',
            changeType: 'changed',
            statusText: 'A matriz de desconto foi alterada'
          });
        }

        const hasRealChanges =
          JSON.stringify(allChanges) !== JSON.stringify(productChanges);
        if (hasRealChanges) {
          setProductChanges(allChanges);
          setHasChanges(allChanges.some((p) => p.status !== 'unchanged'));
        }
      }
    }, [isEditing, catalogData, products, discountMatrix, productChanges]);

    useEffect(() => {
      if (
        isEditing &&
        catalogData &&
        !isInitialized &&
        priceCatalogs.length > 0
      ) {
        setValue('catalogId', catalogData.catalogId);

        getProductsByPriceCatalogId(catalogData.catalogId);
        getDiscountMatrixByCatalogId(catalogData.catalogId);

        setValue('finalDiscount', parseFloat(catalogData.finalDiscount) || 0);
        setValue(
          'taxes',
          catalogData.taxesAndTariffs
            ? catalogData.taxesAndTariffs.toString().replace('.', ',')
            : '0,00'
        );

        setValue(
          'isToUseDiscountOnTotalValue',
          catalogData.isUsingFinalDiscount === true
        );
        setValue('discountOnlyOnSum', catalogData.discountOnlyOnSum === true);

        setValue(
          'finalValue',
          catalogData.totalValueWithoutTaxes
            ? catalogData.totalValueWithoutTaxes.toString().replace('.', ',')
            : '0,00'
        );
        setValue(
          'finalValueWithTaxes',
          catalogData.totalValue
            ? catalogData.totalValue.toString().replace('.', ',')
            : '0,00'
        );

        setIsInitialized(true);
      }
    }, [
      isEditing,
      catalogData,
      isInitialized,
      priceCatalogs,
      setValue,
      getProductsByPriceCatalogId,
      getDiscountMatrixByCatalogId
    ]);

    useEffect(() => {
      if (
        isEditing &&
        catalogData &&
        products.length > 0 &&
        !isProductInitialized
      ) {
        const productIds = catalogData.products.map(
          (product) => product.productId
        );
        setValue('products', productIds);

        const productItems = catalogData.products.map((item) => {
          const productInfo = products.find((p) => p.id === item.productId);
          const price = productInfo
            ? parseFloat(
                isEditing ? item.basePrice : productInfo.catalogPrice
              ) || 0
            : 0;

          const quantity = item.quantity || 1;
          const discount = parseFloat(item.extraDiscount || '0');

          // Calcula o valor unitário baseado no preço, quantidade e desconto
          let activeDiscountMatrix = catalogData?.priceMatrix || [];
          let matrixDiscount = 0;

          if (activeDiscountMatrix && activeDiscountMatrix.length > 0) {
            matrixDiscount = activeDiscountMatrix.reduce((acc, d) => {
              if (
                d.type === 'between' &&
                quantity >= d.minQuantity &&
                (d.maxQuantity === null || quantity <= d.maxQuantity)
              ) {
                return parseFloat(d.discount);
              }
              if (d.type === 'above' && quantity >= d.minQuantity) {
                return parseFloat(d.discount);
              }
              return acc;
            }, 0);
          }

          const matrixDiscountFactor = matrixDiscount / 100;
          const priceAfterMatrixDiscount = price * (1 - matrixDiscountFactor);
          const discountFactor = discount / 100;
          const unitPrice = priceAfterMatrixDiscount * (1 - discountFactor);

          return {
            productId: item.productId,
            productName: item.productName,
            price: price,
            quantity: quantity,
            discount: discount,
            unitPrice: unitPrice.toFixed(2),
            subtotal: parseFloat(item.subtotal || '0').toFixed(2),
            isActive: item?.isActive || true
          };
        });

        if (fields.length > 0) {
          for (let i = fields.length - 1; i >= 0; i--) {
            remove(i);
          }
        }

        productItems.forEach((item) => {
          append(item);
        });

        setIsProductInitialized(true);
      }
    }, [
      isEditing,
      catalogData,
      products,
      fields,
      append,
      remove,
      setValue,
      isProductInitialized
    ]);

    useEffect(() => {
      if (fields.length > 0) {
        const uniqueProducts = new Set();
        const filteredFields = fields.filter((field) => {
          if (uniqueProducts.has(field.productId)) {
            return false;
          } else {
            uniqueProducts.add(field.productId);
            return true;
          }
        });
        if (filteredFields.length !== fields.length) {
          setValue('productItems', filteredFields);
          setValue(
            'products',
            filteredFields.map((field) => field.productId)
          );
        }
      }
    }, [fields]);

    useEffect(() => {
      if (watchCatalogId) {
        const fetchCatalogData = async () => {
          try {
            setProducts([]);
            setDiscountMatrix([]);
            if (!isEditing || watchCatalogId !== catalogData?.catalogId) {
              setValue('products', []);
              setValue('productItems', []);
            }
            setProductChanges([]);
            setHasChanges(false);

            await Promise.all([
              getProductsByPriceCatalogId(watchCatalogId),
              getDiscountMatrixByCatalogId(watchCatalogId)
            ]);
          } catch (error) {
            toast.error('Erro ao carregar dados do catálogo');
          }
        };

        fetchCatalogData();
      }
    }, [
      watchCatalogId,
      isEditing,
      catalogData?.catalogId,
      getDiscountMatrixByCatalogId,
      setValue
    ]);

    useEffect(() => {
      getCatalogChangesHistory();
    }, [getCatalogChangesHistory]);

    useEffect(() => {
      if (!watchProductItems || watchProductItems.length === 0) {
        setValue('finalValue', '0,00');
        setValue('finalValueWithTaxes', '0,00');
        return;
      }

      const totalProducts = watchProductItems.reduce((total, item) => {
        return total + parseFloat(item.subtotal || 0);
      }, 0);

      const taxesValue = parseFloat(
        watch('taxes')
          ?.replace(/[^\d,-]/g, '')
          .replace(',', '.') || 0
      );

      const finalDiscountPercent = parseFloat(watch('finalDiscount') || 0);

      let productSums = parseFloat(totalProducts.toFixed(2));
      let finalValue = parseFloat(totalProducts.toFixed(2));

      if (watch('discountOnlyOnSum')) {
        productSums = totalProducts * (1 - finalDiscountPercent / 100);
      }

      finalValue = productSums;

      setValue('finalValue', productSums.toFixed(2).replace('.', ','));

      let finalValueWithTaxes = finalValue + taxesValue;

      if (watch('isToUseDiscountOnTotalValue')) {
        finalValueWithTaxes =
          finalValueWithTaxes * (1 - finalDiscountPercent / 100);
      }

      setValue(
        'finalValueWithTaxes',
        Math.max(0, finalValueWithTaxes).toFixed(2).replace('.', ',')
      );
    }, [watchProductItems, watch, setValue]);

    useEffect(() => {
      if (
        !watchSelectedProducts ||
        !Array.isArray(watchSelectedProducts) ||
        !products.length
      )
        return;

      const currentItems = {};
      fields.forEach((field) => {
        if (field.productId) {
          currentItems[field.productId] = true;
        }
      });

      for (let i = fields.length - 1; i >= 0; i--) {
        const field = fields[i];
        if (
          field.productId &&
          !watchSelectedProducts.includes(field.productId) &&
          !productChanges.some(
            (p) => p.productId === field.productId && p.status === 'removed'
          )
        ) {
          remove(i);
        }
      }

      watchSelectedProducts.forEach((productId) => {
        if (!currentItems[productId]) {
          const product = products.find((p) => p.id === productId);
          if (product) {
            const price = parseFloat(product.catalogPrice) || 0;
            const quantity = 1;
            const discount = 0;

            // Calcula o valor unitário inicial (mesmo que o preço de catálogo sem descontos)
            const unitPrice = price;

            append({
              productId: product.id,
              productName: product.name,
              price: price,
              quantity: quantity,
              discount: discount,
              unitPrice: unitPrice.toFixed(2),
              subtotal: price.toFixed(2)
            });
          }
        }
      });
    }, [watchSelectedProducts, products, fields, append, remove]);

    useEffect(() => {
      getCatalogs();
    }, [getCatalogs]);

    useEffect(() => {
      if (proposalId) {
        ProposalService.getProposalById(proposalId)
          .then((response) => {
            const data = response.data.data || response.data;
            setProposalData(data);
          })
          .catch(() => {
            toast.error('Erro ao carregar dados da proposta');
          });
      }
    }, [proposalId]);

    useEffect(() => {
      if (watchProductItems && watchProductItems.length > 0) {
        recalculateAllProductSubtotals();
      }
    }, [useUpdatedDiscountMatrix]);

    const getActiveDiscountMatrix = useCallback(() => {
      let activeDiscountMatrix = useUpdatedDiscountMatrix
        ? discountMatrix
        : catalogData?.priceMatrix || [];

      if (!catalogData) activeDiscountMatrix = discountMatrix || [];

      return activeDiscountMatrix;
    }, [useUpdatedDiscountMatrix, discountMatrix, catalogData]);

    const calculateItemSubtotal = useCallback(
      (index, quantity, discount) => {
        const price = parseFloat(
          getValues(`productItems[${index}].unitPrice`) || 0
        );
        quantity = parseInt(quantity, 10) || 1;
        discount = parseFloat(discount) || 0;

        let activeDiscountMatrix = useUpdatedDiscountMatrix
          ? discountMatrix
          : catalogData?.priceMatrix || [];

        if (!catalogData) activeDiscountMatrix = discountMatrix || [];

        let matrixDiscount = 0;
        if (activeDiscountMatrix && activeDiscountMatrix.length > 0) {
          matrixDiscount = activeDiscountMatrix.reduce((acc, d) => {
            if (
              d.type === 'between' &&
              quantity >= d.minQuantity &&
              (d.maxQuantity === null || quantity <= d.maxQuantity)
            ) {
              return parseFloat(d.discount);
            }
            if (d.type === 'above' && quantity >= d.minQuantity) {
              return parseFloat(d.discount);
            }
            return acc;
          }, 0);
        }

        const matrixDiscountFactor = matrixDiscount / 100;
        const afterMatrixDiscount =
          price * quantity * (1 - matrixDiscountFactor);

        const discountFactor = discount / 100;
        const subtotal = afterMatrixDiscount * (1 - discountFactor);

        return subtotal.toFixed(2);
      },
      [
        useUpdatedDiscountMatrix,
        discountMatrix,
        catalogData?.priceMatrix,
        fields,
        getValues
      ]
    );

    const calculateUnitPriceFromDiscount = useCallback(
      (index, quantity, discount) => {
        const catalogPrice = parseFloat(fields[index]?.price || 0);
        quantity = parseInt(quantity, 10) || 1;
        discount = parseFloat(discount) || 0;

        let activeDiscountMatrix = useUpdatedDiscountMatrix
          ? discountMatrix
          : catalogData?.priceMatrix || [];

        if (!catalogData) activeDiscountMatrix = discountMatrix || [];

        let matrixDiscount = 0;
        if (activeDiscountMatrix && activeDiscountMatrix.length > 0) {
          matrixDiscount = activeDiscountMatrix.reduce((acc, d) => {
            if (
              d.type === 'between' &&
              quantity >= d.minQuantity &&
              (d.maxQuantity === null || quantity <= d.maxQuantity)
            ) {
              return parseFloat(d.discount);
            }
            if (d.type === 'above' && quantity >= d.minQuantity) {
              return parseFloat(d.discount);
            }
            return acc;
          }, 0);
        }

        const matrixDiscountFactor = matrixDiscount / 100;
        const priceAfterMatrixDiscount =
          catalogPrice * (1 - matrixDiscountFactor);

        const discountFactor = discount / 100;
        const unitPrice = priceAfterMatrixDiscount * (1 - discountFactor);

        return unitPrice.toFixed(2);
      },
      [
        useUpdatedDiscountMatrix,
        discountMatrix,
        catalogData?.priceMatrix,
        fields
      ]
    );

    const getMatrixDiscountForQuantity = useCallback(
      (index, quantity) => {
        const productName = getValues(`productItems[${index}].productName`);

        const productId = products.find((p) => p.name === productName)?.id;
        const activeDiscountMatrix = getActiveDiscountMatrix(productId);

        if (!activeDiscountMatrix || activeDiscountMatrix.length === 0)
          return 0;

        return activeDiscountMatrix.reduce((acc, d) => {
          if (
            d.type === 'between' &&
            quantity >= d.minQuantity &&
            (d.maxQuantity === null || quantity <= d.maxQuantity)
          ) {
            return parseFloat(d.discount);
          }
          if (d.type === 'above' && quantity >= d.minQuantity) {
            return parseFloat(d.discount);
          }
          return acc;
        }, 0);
      },
      [
        useUpdatedDiscountMatrix,
        discountMatrix,
        getActiveDiscountMatrix,
        catalogData
      ]
    );

    const recalculateAllProductSubtotals = () => {
      if (!watchProductItems || watchProductItems.length === 0) return;

      watchProductItems.forEach((item, index) => {
        const quantity = parseInt(item.quantity, 10) || 1;
        const discount = parseFloat(item.discount) || 0;

        const subtotal = calculateItemSubtotal(index, quantity, discount);
        setValue(`productItems[${index}].subtotal`, subtotal);
      });

      calculateTotalValue();
    };

    const calculateTotalValue = () => {
      if (!watchProductItems || watchProductItems.length === 0) {
        return '0.00';
      }

      return watchProductItems
        .reduce((total, item) => {
          return total + parseFloat(item.subtotal || 0);
        }, 0)
        .toFixed(2);
    };

    const handleGenerateCatalogPDF = async () => {
      try {
        const currentItems = getValues('productItems');
        const itemsSource =
          currentItems && Array.isArray(currentItems) && currentItems.length > 0
            ? currentItems
            : fields;

        if (!itemsSource || itemsSource.length === 0) {
          toast.error('Selecione pelo menos um produto para gerar o PDF');
          return;
        }
        const normalizeMoney = (val) => {
          if (val === null || val === undefined) return 0;
          const s = String(val).trim();
          if (s === '') return 0;
          let cleaned = s.replace(/[^0-9.,-]/g, '');
          if (cleaned.includes(',') && cleaned.includes('.')) {
            cleaned = cleaned.replace(/\./g, '').replace(',', '.');
          } else if (cleaned.includes(',')) {
            cleaned = cleaned.replace(',', '.');
          }
          const n = parseFloat(cleaned);
          return Number.isNaN(n) ? 0 : n;
        };

        const productsForPdf = itemsSource.map((item, index) => {
          const name = item.productName || item.name || '';
          const quantity = parseInt(item.quantity, 10) || 1;

          let totalValue = normalizeMoney(item.subtotal);
          if (!totalValue || Number.isNaN(totalValue)) {
            const discount = parseFloat(item.discount) || 0;
            const calc = calculateItemSubtotal(index, quantity, discount);
            totalValue = normalizeMoney(calc);
          }

          // Usa o campo unitPrice do item, que já contém o valor unitário correto
          // Se não existir, calcula dividindo o subtotal pela quantidade
          let unitValue = normalizeMoney(item.unitPrice);
          if (!unitValue || Number.isNaN(unitValue)) {
            unitValue = quantity > 0 ? totalValue / quantity : totalValue;
          }

          return {
            name,
            quantity,
            unitValue,
            totalValue
          };
        });

        if (!productsForPdf || productsForPdf.length === 0) {
          toast.error('Falha ao montar a tabela de produtos para o PDF');
          return;
        }

        const subtotalFromItems = productsForPdf.reduce(
          (acc, p) => acc + (Number(p.totalValue) || 0),
          0
        );
        const finalValueFromForm = normalizeMoney(getValues('finalValue'));
        const finalValueWithTaxesFromForm = normalizeMoney(
          getValues('finalValueWithTaxes')
        );

        const textoOriginal = proposalData?.informationsClient || '';

        const fetchCustomFieldsMetadata = async () => {
          const customFieldsLabels = {};

          if (accountData?.recordTypeId) {
            try {
              const accountFields =
                await CustomFieldsService.getFieldsByRecordType(
                  'ACCOUNT',
                  accountData.recordTypeId
                );
              accountFields.data.data.forEach((field) => {
                customFieldsLabels[field.name] = field.label;
              });
            } catch (error) {
              console.warn('Erro ao buscar custom fields da conta:', error);
            }
          }

          if (contactData?.recordTypeId) {
            try {
              const contactFields =
                await CustomFieldsService.getFieldsByRecordType(
                  'CONTACT',
                  contactData.recordTypeId
                );
              contactFields.data.data.forEach((field) => {
                customFieldsLabels[field.name] = field.label;
              });
            } catch (error) {
              console.warn('Erro ao buscar custom fields do contato:', error);
            }
          }

          return customFieldsLabels;
        };

        const customFieldsLabels = await fetchCustomFieldsMetadata();

        const clientData = {
          account: accountData || {},
          contact: contactData || {},
          customFieldsLabels: customFieldsLabels || {},
          template: textoOriginal || ''
        };

        const informacoesProcessadas = textoOriginal
          ? processProposalVariables(
              textoOriginal,
              {
                account: accountData || {},
                contact: contactData || {}
              },
              true,
              customFieldsLabels
            )
          : '';

        let logoUrl = null;
        if (proposalId) {
          try {
            const logoResponse = await ProposalService.getProposalLogo(
              proposalId
            );
            const logoBlob = logoResponse?.data;
            if (logoBlob) {
              logoUrl = URL.createObjectURL(logoBlob);
            }
          } catch (err) {
            console.log('Nenhum logo encontrado para esta proposta');
          }
        }

        const dataForPdf = {
          name: proposalData?.title || 'Orçamento do Catálogo',
          title: proposalData?.title || '',
          informations: proposalData?.informations || '',
          clientData: clientData,
          informationsClient: informacoesProcessadas,
          validity: proposalData?.validity || '',
          products: productsForPdf,
          subtotalFromItems,
          finalValueFromForm,
          finalValueWithTaxesFromForm,
          logoUrl
        };

        setPdfData(dataForPdf);
        setRenderPdf(true);
      } catch (e) {
        toast.error('Não foi possível gerar o PDF do catálogo');
      }
    };

    useEffect(() => {
      if (!renderPdf || !pdfData) return;
      const el = printRef.current;
      if (!el) return;

      const opt = {
        margin: 10,
        filename: 'orcamento_catalogo.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      const runner = html2pdf || (window && window.html2pdf);
      if (!runner) {
        toast.error('Biblioteca de PDF não carregada');
        setRenderPdf(false);
        return;
      }

      setTimeout(() => {
        const waitForImages = () => {
          return new Promise((resolve) => {
            const images = el.querySelectorAll('img');
            if (images.length === 0) {
              resolve();
              return;
            }

            let loadedCount = 0;
            const checkAllLoaded = () => {
              loadedCount++;
              if (loadedCount === images.length) {
                resolve();
              }
            };

            images.forEach((img) => {
              if (img.complete) {
                checkAllLoaded();
              } else {
                img.onload = checkAllLoaded;
                img.onerror = () => {
                  checkAllLoaded();
                };
              }
            });

            setTimeout(() => {
              resolve();
            }, 5000);
          });
        };

        waitForImages().then(() => {
          runner()
            .set(opt)
            .from(el)
            .save()
            .then(() => {
              setRenderPdf(false);
              if (pdfData.logoUrl) {
                URL.revokeObjectURL(pdfData.logoUrl);
              }
            })
            .catch(() => {
              setRenderPdf(false);
              if (pdfData.logoUrl) {
                URL.revokeObjectURL(pdfData.logoUrl);
              }
              toast.error('Erro ao gerar PDF');
            });
        });
      }, 50);
    }, [renderPdf, pdfData]);

    const HiddenPdfRenderer =
      renderPdf && pdfData
        ? React.createElement(
            'div',
            {
              style: {
                position: 'absolute',
                left: '-9999px',
                top: 0,
                width: '794px'
              }
            },
            React.createElement(
              'div',
              { ref: printRef },
              React.createElement(CatalogBudgetPrintable, { data: pdfData })
            )
          )
        : null;

    const formatProductsAndCatalogsToShow = useCallback(
      (data) => {
        data.catalog = {};

        if (isEditing && catalogData) {
          data.catalog.id = catalogData.id;
        }

        data.catalog.catalogId = data.catalogId;
        data.catalog.finalDiscount = parseFloat(data.finalDiscount || 0);
        data.catalog.isUsingFinalDiscount =
          data.isToUseDiscountOnTotalValue === true;

        data.catalog.shouldUpdateMatrix = useUpdatedDiscountMatrix;

        data.catalog.discountOnlyOnSum = data.discountOnlyOnSum === true;

        data.catalog.taxesAndTariffs = parseFloat(
          data.taxes?.replace(/[^\d,-]/g, '').replace(',', '.') || 0
        );

        if (data.finalValue) {
          data.catalog.totalValueWithoutTaxes = parseFloat(
            data.finalValue.replace(/[^\d,-]/g, '').replace(',', '.') || 0
          );
        }

        if (data.finalValueWithTaxes) {
          data.catalog.totalValueWithTaxes = parseFloat(
            data.finalValueWithTaxes
              .replace(/[^\d,-]/g, '')
              .replace(',', '.') || 0
          );
        }

        if (data.productItems && data.productItems.length > 0) {
          if (products && products.length > 0) {
            data.catalog.products = data.productItems.map((item) => {
              let productId = item.productId;

              if (!productId) {
                const matchingProduct = products.find(
                  (p) => p.name === item.productName
                );
                if (matchingProduct) {
                  productId = matchingProduct.id;
                }
              }

              if (!productId && catalogData && catalogData.products) {
                const matchingCatalogProduct = catalogData.products.find(
                  (p) => p.productName === item.productName
                );
                if (matchingCatalogProduct) {
                  productId = matchingCatalogProduct.productId;
                }
              }

              const isUsingOldPrice = productChanges.some(
                (p) =>
                  p.productId === productId &&
                  (p.status === 'changed' || p.status === 'removed')
              );

              return {
                productId: productId,
                productName: item.productName,
                basePrice: parseFloat(item.price) || 0,
                quantity: parseInt(item.quantity, 10) || 1,
                subtotal: parseFloat(item.subtotal) || 0,
                extraDiscounts: parseFloat(item.discount) || 0,
                isUsingOldPrice
              };
            });
          }
        }

        return removeFields(data, [
          'catalogId',
          'discountOnlyOnSum',
          'productItems',
          'taxes',
          'products',
          'isToUseDiscountOnTotalValue',
          'finalValue',
          'finalValueWithTaxes',
          'finalDiscount'
        ]);
      },
      [products, useUpdatedDiscountMatrix]
    );
    useImperativeHandle(ref, () => ({
      formatProductsAndCatalogsToShow: formatProductsAndCatalogsToShow,
      forceUpdate: () => {
        setIsInitialized(false);
        setIsProductInitialized(false);
      },
      generatePDF: handleGenerateCatalogPDF
    }));

    const handleUpdateProducts = () => {
      if (
        !products ||
        products.length === 0 ||
        !fields ||
        fields.length === 0
      ) {
        toast.error('Não foi possível atualizar os produtos.');
        return;
      }

      let updatedCount = 0;

      const updatedProducts = catalogData?.products.map((product) => {
        const updatedProductInfo = products.find(
          (p) => p.id === product.productId
        );

        if (updatedProductInfo) {
          const newPrice = parseFloat(updatedProductInfo.catalogPrice) || 0;
          const quantity = parseInt(product.quantity, 10) || 1;
          const discount = parseFloat(product.extraDiscount || 0) || 0;

          const matrixToUse = useUpdatedDiscountMatrix
            ? discountMatrix
            : catalogData?.priceMatrix || [];
          let matrixDiscount = 0;

          if (matrixToUse && matrixToUse.length > 0) {
            matrixDiscount = matrixToUse.reduce((acc, d) => {
              if (
                d.type === 'between' &&
                quantity >= d.minQuantity &&
                (d.maxQuantity === null || quantity <= d.maxQuantity)
              ) {
                return parseFloat(d.discount);
              }
              if (d.type === 'above' && quantity >= d.minQuantity) {
                return parseFloat(d.discount);
              }
              return acc;
            }, 0);
          }

          const matrixDiscountFactor = matrixDiscount / 100;
          const afterMatrixDiscount =
            newPrice * quantity * (1 - matrixDiscountFactor);
          const discountFactor = discount / 100;
          const subtotal = (afterMatrixDiscount * (1 - discountFactor)).toFixed(
            2
          );

          updatedCount++;

          return {
            ...product,
            basePrice: newPrice.toFixed(2),
            subtotal
          };
        }

        return product;
      });

      if (updatedCount > 0) {
        setCatalogData({
          ...catalogData,
          products: updatedProducts
        });

        setIsProductInitialized(false);

        setProductChanges([]);
        setHasChanges(false);
        setShowChangesDetail(false);
      } else {
        toast.info('Nenhum produto precisou ser atualizado.');
      }
    };

    const matrixChanged = useMemo(() => {
      return productChanges.some(
        (change) => change.statusText === 'A matriz de desconto foi alterada'
      );
    }, [productChanges]);

    const changedProductsCount = useMemo(() => {
      return productChanges.filter(
        (change) =>
          change.status === 'changed' &&
          change.statusText !== 'A matriz de desconto foi alterada'
      ).length;
    }, [productChanges]);

    const removedProductsCount = useMemo(() => {
      return productChanges.filter(
        (change) => change.status === 'removed' && change.productId
      ).length;
    }, [productChanges]);

    const handleShowProductDetails = (productId) => {
      setDetailModalState({ isOpen: true, productId: productId });
    };

    const toggleProductDetailModal = () => {
      setDetailModalState({ isOpen: false, productId: null });
    };

    return (
      <>
        {HiddenPdfRenderer}
        <ProductsFieldsInfModal
          isOpen={detailModalState.isOpen}
          toggle={toggleProductDetailModal}
          productId={detailModalState.productId}
        />
        <hr />
        <Row className="mb-3 align-items-center">
          <Col>
            <h3 className="mb-1">Catálogos de Preço e Produtos</h3>
          </Col>
          {proposalId && (
            <Col className="text-right">
              <Button color="info" onClick={handleGenerateCatalogPDF}>
                <i className="fas fa-file-pdf mr-2" />
                Baixar PDF
              </Button>
            </Col>
          )}
        </Row>

        {isEditing && hasChanges && (
          <ChangesDetailModal
            isOpen={showChangesDetail}
            toggle={() => setShowChangesDetail(false)}
            removedProducts={productChanges.filter(
              (p) => p.status === 'removed' && p.productId
            )}
            changedProducts={productChanges.filter(
              (p) => p.status === 'changed' && p.productId
            )}
            originalMatrix={catalogData?.priceMatrix || []}
            updatedMatrix={discountMatrix}
            discountMatrixChanged={productChanges.some(
              (p) => p.statusText === 'A matriz de desconto foi alterada'
            )}
            catalogChangesHistory={catalogChangesHistory}
            onUpdateProducts={handleUpdateProducts}
            useUpdatedDiscountMatrix={useUpdatedDiscountMatrix}
            onApplyUpdatedMatrix={() => {
              setUseUpdatedDiscountMatrix(true);
              recalculateAllProductSubtotals(catalogData?.priceMatrix || []);
            }}
            onRevertToOriginalMatrix={() => {
              setUseUpdatedDiscountMatrix(false);
              recalculateAllProductSubtotals(discountMatrix);
            }}
          />
        )}

        <Row className="mb-3">
          <Col sm="12" md="6">
            <label className="form-control-label">Catálogos de Preço</label>
            <Controller
              render={(props) => (
                <FlexSelect
                  dataOptions={priceCatalogs || []}
                  isMulti={false}
                  value={props.value}
                  valueController={setValue}
                  fieldName="catalogId"
                  labelName="name"
                  valueName="id"
                  isClearable={true}
                  closeMenuOnSelect={true}
                />
              )}
              control={control}
              defaultValue={
                isEditing && catalogData ? catalogData?.catalogId : null
              }
              name="catalogId"
            />
          </Col>
          <Col className="mb-3">
            <label className="form-control-label">Desconto Final(%)</label>
            <Controller
              render={(props) => (
                <InputGroup>
                  <Input
                    value={props.value || 0}
                    onChange={(e) => {
                      const newValue = Math.min(
                        100,
                        Math.max(0, parseFloat(e.target.value) || 0)
                      );
                      props.onChange(newValue);
                    }}
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                  <InputGroupAddon addonType="append">
                    <InputGroupText>%</InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
              )}
              control={control}
              defaultValue={
                isEditing && catalogData
                  ? parseFloat(catalogData?.finalDiscount) || 0
                  : 0
              }
              name="finalDiscount"
              rules={{ required: true, min: 0, max: 100 }}
            />
          </Col>

          <Col className="mb-3">
            <label className="form-control-label">
              Taxas (Fretes + Encargos)
            </label>
            <Controller
              render={(props) => (
                <CurrencyMask
                  valueController={setValue}
                  value={props.value}
                  fieldName="taxes"
                />
              )}
              control={control}
              name="taxes"
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col sm="12" md="6">
            <label className="form-control-label">Produtos do Catálogo</label>
            <Controller
              render={(props) => (
                <FlexSelect
                  dataOptions={products || []}
                  isMulti={true}
                  value={props.value}
                  valueController={setValue}
                  fieldName="products"
                  labelName="name"
                  valueName="id"
                  isDisabled={!watchCatalogId}
                  isClearable={true}
                  closeMenuOnSelect={false}
                />
              )}
              control={control}
              defaultValue={[]}
              name="products"
            />
          </Col>
          <Col className="mb-3 ml-3">
            <Controller
              name="discountOnlyOnSum"
              control={control}
              defaultValue={
                isEditing && catalogData
                  ? catalogData.discountOnlyOnSum === false
                  : false
              }
              render={(props) => (
                <>
                  <Input
                    type="checkbox"
                    onChange={(e) => {
                      props.onChange(e.target.checked);
                      if (e.target.checked) {
                        setValue('isToUseDiscountOnTotalValue', false);
                      }
                    }}
                    disabled={controlIsToUseDiscountOnTotalValue}
                    checked={props.value}
                  />
                  <label className="form-control-label">
                    Desconto apenas no somatório do produto
                  </label>
                </>
              )}
            />
          </Col>
          <Col className="mb-3">
            <Controller
              name="isToUseDiscountOnTotalValue"
              control={control}
              defaultValue={
                isEditing && catalogData
                  ? catalogData.isUsingFinalDiscount === true
                  : false
              }
              render={(props) => (
                <>
                  <Input
                    type="checkbox"
                    onChange={(e) => {
                      props.onChange(e.target.checked);
                      if (e.target.checked) {
                        setValue('discountOnlyOnSum', false);
                      }
                    }}
                    disabled={controlDiscountOnlyOnSum}
                    checked={props.value}
                  />
                  <label className="form-control-label">
                    Desconto no valor final
                  </label>
                </>
              )}
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <label className="form-control-label">Valor Total(Sem Taxas)</label>
            <Controller
              render={(props) => (
                <CurrencyMask
                  valueController={setValue}
                  value={props.value}
                  fieldName="finalValue"
                  shouldDisable={true}
                />
              )}
              control={control}
              defaultValue=""
              name="finalValue"
            />
          </Col>
          <Col>
            <label className="form-control-label">Valor Final(Com Taxas)</label>
            <Controller
              render={(props) => (
                <CurrencyMask
                  valueController={setValue}
                  value={props.value}
                  fieldName="finalValueWithTaxes"
                  shouldDisable={true}
                />
              )}
              control={control}
              defaultValue=""
              name="finalValueWithTaxes"
            />
          </Col>
        </Row>

        {fields.length > 0 && (
          <Row className="mb-3">
            <Col sm="12" md="12">
              <h4 className="mb-1">
                Produtos Selecionados
                {hasChanges && (
                  <Badge color="warning" className="ml-2">
                    Alterações detectadas
                  </Badge>
                )}
              </h4>
              <Table bordered responsive>
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th style={{ width: '120px' }}>Quantidade</th>
                    <th style={{ width: '130px' }}>Preço Catálogo (R$)</th>
                    <th style={{ width: '130px' }}>Valor Unitário (R$)</th>
                    <th style={{ width: '180px' }}>Desconto (%)</th>
                    <th style={{ width: '150px' }}>Subtotal (R$)</th>
                    <th style={{ width: '60px', textAlign: 'center' }}>
                      Detalhes
                    </th>
                    {isEditing && hasChanges && (
                      <th style={{ width: '60px' }}>Status</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, index) => {
                    const productChange = productChanges
                      ? productChanges.find(
                          (p) => p.productId === field.productId
                        )
                      : null;

                    const isRemoved =
                      productChange && productChange.status === 'removed';

                    return (
                      <tr
                        key={field.id}
                        className={
                          productChange && productChange.status !== 'unchanged'
                            ? `table-row-${
                                productChange.status === 'removed'
                                  ? 'danger'
                                  : 'warning'
                              }`
                            : ''
                        }
                      >
                        <td>
                          <div className="d-flex align-items-center">
                            <Controller
                              render={(props) => (
                                <Input
                                  value={props.value || ''}
                                  disabled
                                  type="text"
                                />
                              )}
                              control={control}
                              defaultValue={field.productName || ''}
                              name={`productItems[${index}].productName`}
                            />
                          </div>
                        </td>
                        <td>
                          <Controller
                            render={(props) => (
                              <Input
                                value={props.value}
                                disabled={isRemoved}
                                onFocus={(e) => e.target.select()}
                                onBlur={(e) => {
                                  if (!e.target.value) {
                                    props.onChange(1);
                                  }
                                }}
                                onChange={(e) =>
                                  dispatchProductAction({
                                    type: ACTION_TYPES.CHANGE_QUANTITY,
                                    index,
                                    value: e.target.value,
                                    onChange: props.onChange
                                  })
                                }
                                type="number"
                                min="1"
                                step="1"
                              />
                            )}
                            control={control}
                            defaultValue={field.quantity || 1}
                            name={`productItems[${index}].quantity`}
                            rules={{ required: true, min: 1 }}
                          />
                        </td>
                        <td>
                          <Controller
                            render={(props) => (
                              <Input
                                value={
                                  props.value
                                    ? parseFloat(props.value).toFixed(2)
                                    : '0.00'
                                }
                                disabled
                                type="text"
                                style={{
                                  backgroundColor: '#f0f0f0',
                                  fontWeight: 'bold'
                                }}
                              />
                            )}
                            control={control}
                            defaultValue={field.price || 0}
                            name={`productItems[${index}].price`}
                          />
                        </td>
                        <td>
                          <Controller
                            render={(props) => (
                              <Input
                                value={props.value}
                                disabled={isRemoved}
                                onFocus={(e) => e.target.select()}
                                onBlur={(e) => {
                                  if (!e.target.value) {
                                    props.onChange('0.00');
                                  }
                                }}
                                onChange={(e) =>
                                  dispatchProductAction({
                                    type: ACTION_TYPES.CHANGE_UNIT_PRICE,
                                    index,
                                    value: e.target.value,
                                    onChange: props.onChange
                                  })
                                }
                                type="number"
                                min="0"
                                step="0.01"
                              />
                            )}
                            control={control}
                            defaultValue={
                              field.unitPrice ||
                              calculateUnitPriceFromDiscount(
                                index,
                                field.quantity || 1,
                                field.discount || 0
                              )
                            }
                            name={`productItems[${index}].unitPrice`}
                          />
                        </td>
                        <td>
                          <Controller
                            render={(props) => (
                              <InputGroup>
                                <Input
                                  value={props.value}
                                  disabled={isRemoved}
                                  onFocus={(e) => e.target.select()}
                                  onBlur={(e) => {
                                    if (
                                      !e.target.value ||
                                      e.target.value === '-'
                                    ) {
                                      props.onChange(0);
                                    }
                                  }}
                                  onChange={(e) =>
                                    dispatchProductAction({
                                      type: ACTION_TYPES.CHANGE_DISCOUNT,
                                      index,
                                      value: e.target.value,
                                      onChange: props.onChange
                                    })
                                  }
                                  type="number"
                                  min="-9999"
                                  max="100"
                                  step="0.01"
                                  style={
                                    parseFloat(props.value || 0) < 0
                                      ? { color: 'red', fontWeight: 'bold' }
                                      : parseFloat(props.value || 0) > 0
                                      ? { color: 'green', fontWeight: 'bold' }
                                      : {}
                                  }
                                />
                                <InputGroupAddon addonType="append">
                                  <InputGroupText>%</InputGroupText>
                                </InputGroupAddon>
                              </InputGroup>
                            )}
                            control={control}
                            defaultValue={field.discount || 0}
                            name={`productItems[${index}].discount`}
                            rules={{ required: true, min: -9999, max: 100 }}
                          />
                        </td>
                        <td>
                          <Controller
                            render={(props) => (
                              <Input
                                value={props.value}
                                disabled={isRemoved}
                                onFocus={(e) => e.target.select()}
                                onBlur={(e) => {
                                  if (!e.target.value) {
                                    props.onChange('0.00');
                                  }
                                }}
                                onChange={(e) =>
                                  dispatchProductAction({
                                    type: ACTION_TYPES.CHANGE_SUBTOTAL,
                                    index,
                                    value: e.target.value,
                                    onChange: props.onChange
                                  })
                                }
                                type="number"
                                min="0"
                                step="0.01"
                              />
                            )}
                            control={control}
                            defaultValue={field.subtotal || '0.00'}
                            name={`productItems[${index}].subtotal`}
                          />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <Button
                            color="info"
                            size="sm"
                            className="btn-icon"
                            onClick={() =>
                              handleShowProductDetails(field.productId)
                            }
                          >
                            <i className="fas fa-info-circle"></i>
                          </Button>
                        </td>
                        <td>
                          {isEditing && hasChanges && (
                            <ProductChangeIndicator
                              product={productChange}
                              onRemove={() => remove(index)}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td
                      colSpan={isEditing && hasChanges ? '5' : '4'}
                      className="text-right"
                    >
                      <strong>Total:</strong>
                    </td>
                    <td>
                      <strong>R$ {calculateTotalValue()}</strong>
                    </td>
                  </tr>
                </tbody>
              </Table>

              {isEditing && hasChanges && (
                <AlertBox type="warning">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    <span>
                      {matrixChanged && (
                        <>
                          A matriz de desconto foi alterada desde a criação
                          deste lead.
                          <br />
                        </>
                      )}
                      {changedProductsCount > 0 && (
                        <>
                          {changedProductsCount} produto
                          {changedProductsCount > 1 ? 's' : ''}{' '}
                          {changedProductsCount > 1
                            ? 'foram alterados'
                            : 'foi alterado'}
                          .
                        </>
                      )}
                      {removedProductsCount > 0 && (
                        <>
                          {removedProductsCount} produto
                          {removedProductsCount > 1 ? 's' : ''}{' '}
                          {removedProductsCount > 1
                            ? 'foram removidos'
                            : 'foi removido'}
                          .
                        </>
                      )}
                    </span>
                    <div className="ml-auto">
                      <Button
                        color="link"
                        size="sm"
                        className="mr-2 p-0"
                        onClick={() => setShowChangesDetail(true)}
                      >
                        Ver detalhes
                      </Button>
                      {useUpdatedDiscountMatrix ? (
                        <Badge color="success">
                          Utilizando matriz de descontos atualizada
                        </Badge>
                      ) : (
                        <Badge color="danger">
                          Utilizando matriz de descontos original
                        </Badge>
                      )}
                    </div>
                  </div>
                </AlertBox>
              )}
            </Col>
          </Row>
        )}
        <hr />
      </>
    );
  }
);

CatalogProductsForm.displayName = 'CatalogProductsForm';

export default CatalogProductsForm;
